import { useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useToast } from "../components/ui";
import { type NotificationEvent, useAttention } from "./notifications";
import { keys } from "./queries";

/** What the tenant's Durable Object announces (ids only; data is refetched through the API). */
type LiveEvent =
  | { type: "ready" }
  | { type: "conversation"; id: string }
  | { type: "contact"; id: string }
  | { type: "quick_replies" }
  | { type: "templates" }
  | { type: "company" }
  | { type: "agent" }
  | { type: "lumi" }
  | { type: "products"; id?: string }
  | { type: "knowledge"; id?: string }
  | { type: "sales_points" }
  | { type: "cases"; id?: string }
  | { type: "quotes"; id?: string }
  | ({ type: "notification" } & NotificationEvent)
  | { type: "team" }
  | { type: "support" }
  | { type: "activity" }
  | { type: "contacts" }
  | { type: "agenda" }
  | { type: "removed" };

/** The API closes a removed member's socket with this code; reconnecting would be refused. */
const REMOVED_CLOSE = 4003;

const LiveContext = createContext(false);

/** True while the live inbox socket is connected. Queries poll slowly then, fast otherwise. */
export const useLive = () => useContext(LiveContext);

const RETRY_MS = [1_000, 2_000, 5_000, 10_000, 30_000];
const PING_MS = 25_000;

export function LiveProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const toast = useToast();
  // A ref, so a new handler never reconnects the socket.
  const handler = useAttention();
  const attention = useRef(handler);
  attention.current = handler;
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let attempt = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;
    let stopped = false;

    const onEvent = (event: LiveEvent) => {
      switch (event.type) {
        case "conversation":
          qc.invalidateQueries({ queryKey: ["conversations"] });
          qc.invalidateQueries({ queryKey: keys.conversation(event.id) });
          qc.invalidateQueries({ queryKey: keys.messages(event.id) });
          break;
        case "contact":
          qc.invalidateQueries({ queryKey: ["contacts"] });
          qc.invalidateQueries({ queryKey: keys.contact(event.id) });
          qc.invalidateQueries({ queryKey: ["conversation"] });
          break;
        case "cases":
          qc.invalidateQueries({ queryKey: ["cases"] });
          break;
        case "quotes":
          qc.invalidateQueries({ queryKey: ["quotes"] });
          break;
        case "quick_replies":
          qc.invalidateQueries({ queryKey: keys.quickReplies });
          break;
        case "templates":
          qc.invalidateQueries({ queryKey: ["templates"] });
          break;
        case "products":
          qc.invalidateQueries({ queryKey: ["products"] });
          qc.invalidateQueries({ queryKey: keys.firstSteps });
          break;
        case "knowledge":
          qc.invalidateQueries({ queryKey: ["knowledge"] });
          qc.invalidateQueries({ queryKey: keys.firstSteps });
          break;
        case "sales_points":
          qc.invalidateQueries({ queryKey: ["sales-points"] });
          break;
        case "company":
          qc.invalidateQueries({ queryKey: keys.company });
          qc.invalidateQueries({ queryKey: keys.firstSteps });
          break;
        case "agent":
        case "lumi":
          qc.invalidateQueries({ queryKey: keys.ai });
          break;
        case "team":
          // A role change shows or hides actions right away; agents also see a different inbox.
          qc.invalidateQueries({ queryKey: keys.me });
          qc.invalidateQueries({ queryKey: keys.team });
          qc.invalidateQueries({ queryKey: ["invitations"] });
          qc.invalidateQueries({ queryKey: ["conversations"] });
          qc.invalidateQueries({ queryKey: ["cases"] });
          break;
        case "support":
          qc.invalidateQueries({ queryKey: ["support-access"] });
          break;
        case "contacts":
          qc.invalidateQueries({ queryKey: ["contacts"] });
          qc.invalidateQueries({ queryKey: ["organizations"] });
          break;
        case "agenda":
          qc.invalidateQueries({ queryKey: ["agenda"] });
          break;
        case "activity":
          qc.invalidateQueries({ queryKey: ["activity"] });
          break;
        case "notification":
          qc.invalidateQueries({ queryKey: ["notifications"] });
          attention.current(event);
          break;
        case "removed":
          stopped = true;
          toast("Ya no tienes acceso a esta empresa.");
          qc.invalidateQueries();
          break;
      }
    };

    const connect = () => {
      const scheme = window.location.protocol === "https:" ? "wss" : "ws";
      ws = new WebSocket(`${scheme}://${window.location.host}/api/inbox/ws`);
      ws.onopen = () => {
        attempt = 0;
        setConnected(true);
        // Anything missed while disconnected.
        qc.invalidateQueries();
        ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send("ping"), PING_MS);
      };
      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        try {
          onEvent(JSON.parse(String(e.data)) as LiveEvent);
        } catch {
          // Ignore malformed frames.
        }
      };
      ws.onclose = (e) => {
        setConnected(false);
        clearInterval(ping);
        if (stopped || e.code === REMOVED_CLOSE) return;
        retry = setTimeout(connect, RETRY_MS[Math.min(attempt++, RETRY_MS.length - 1)]);
      };
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(retry);
      clearInterval(ping);
      ws?.close();
    };
  }, [qc, toast]);

  return <LiveContext.Provider value={connected}>{children}</LiveContext.Provider>;
}
